// src/pages/CompanyProfile.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../api/client";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import { AppleComboBox } from "../components/AppleComboBox";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

const DISPLAY_KEYS = {
  "US Dollar": "companyProfile.currencies.usd",
  "Hong Kong Dollar": "companyProfile.currencies.hkd",
  "Chinese Yuan": "companyProfile.currencies.cny",
  "Euro": "companyProfile.currencies.eur",
  "British Pound": "companyProfile.currencies.gbp",
  "Retail": "companyProfile.industries.retail",
  "Trading": "companyProfile.industries.trading",
  "IT/Software": "companyProfile.industries.it",
  "Food & Beverage": "companyProfile.industries.food",
  "English": "companyProfile.languages.english",
  "中文": "companyProfile.languages.chinese",
  "December 31": "companyProfile.yearEnds.december",
  "March 31": "companyProfile.yearEnds.march",
  "June 30": "companyProfile.yearEnds.june",
  "September 30": "companyProfile.yearEnds.september",
  "Service": "sales.common.service",
  "Other": "paymentMethods.options.other",
} as const;

type KVResp = { key: string; value: string };

type CurrencyOpt = { id: string; code: string; name: string };
type PickOpt = { id: string; value: string; label: string };

const CURRENCY_LIST: CurrencyOpt[] = [
  { id: "USD", code: "USD", name: "US Dollar" },
  { id: "HKD", code: "HKD", name: "Hong Kong Dollar" },
  { id: "CNY", code: "CNY", name: "Chinese Yuan" },
  { id: "EUR", code: "EUR", name: "Euro" },
  { id: "GBP", code: "GBP", name: "British Pound" },
];

const INDUSTRIES: PickOpt[] = [
  { id: "retail", value: "retail", label: "Retail" },
  { id: "service", value: "service", label: "Service" },
  { id: "trading", value: "trading", label: "Trading" },
  { id: "it", value: "it", label: "IT/Software" },
  { id: "food", value: "food", label: "Food & Beverage" },
  { id: "other", value: "other", label: "Other" },
];

const LANGUAGES: PickOpt[] = [
  { id: "en", value: "en", label: "English" },
  { id: "zh", value: "zh", label: "中文" },
];

const YEAR_ENDS: PickOpt[] = [
  { id: "12-31", value: "12-31", label: "December 31" },
  { id: "3-31", value: "3-31", label: "March 31" },
  { id: "6-30", value: "6-30", label: "June 30" },
  { id: "9-30", value: "9-30", label: "September 30" },
];

export default function CompanyProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [companyName, setCompanyName] = useState("");
  const [currency, setCurrency] = useState("HKD");
  const [fye, setFye] = useState("12-31");
  const [industry, setIndustry] = useState("retail");
  const [language, setLanguage] = useState("en");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<"save" | null>(null);

  useEffect(() => {
    if (!company_id) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [name, cur, fy, ind, lang] = await Promise.all([
          apiGet<KVResp>(`/settings/company?company_id=${company_id}`),
          apiGet<KVResp>(`/settings/currency?company_id=${company_id}`),
          apiGet<KVResp>(
            `/settings/financial-year-end?company_id=${company_id}`
          ),
          apiGet<KVResp>(`/settings/industry?company_id=${company_id}`),
          apiGet<KVResp>(`/settings/language?company_id=${company_id}`),
        ]);

        if (cancelled) return;

        if (name?.value) setCompanyName(name.value);
        if (cur?.value) setCurrency(cur.value);
        if (fy?.value) setFye(fy.value);
        if (ind?.value) setIndustry(ind.value);
        if (lang?.value) setLanguage(lang.value);
      } catch {
        // keep defaults
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company_id]);

  const selectedCurrency = useMemo(
    () => CURRENCY_LIST.find((c) => c.code === currency) || null,
    [currency]
  );
  const selectedIndustry = useMemo(
    () => INDUSTRIES.find((o) => o.value === industry) || null,
    [industry]
  );
  const selectedLanguage = useMemo(
    () => LANGUAGES.find((o) => o.value === language) || null,
    [language]
  );
  const knownFye = YEAR_ENDS.find((y) => y.value === fye);
  const fyeLabel = knownFye ? t(DISPLAY_KEYS[knownFye.label as keyof typeof DISPLAY_KEYS]) : fye;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company_id || saving) return;

    setSuccess(false);
    setError(null);
    setSaving(true);

    try {
      await Promise.all([
        apiPost("/settings/currency", { value: currency, company_id }),
        apiPost("/settings/industry", { value: industry, company_id }),
        apiPost("/settings/language", { value: language, company_id }),
      ]);
      setSuccess(true);
      setTimeout(() => navigate(companyRoute(company_id, "/settings")), 700);
    } catch {
      setError("save");
    } finally {
      setSaving(false);
    }
  };

  // AppleClasses helpers
  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  // ✅ Put the gap ON the header bar (mb-2), like SalesList / PaymentMethodList
  const headerBar =
    "sticky top-0 z-30 " +
    "bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 " +
    "border-b border-gray-100 " +
    "px-2 sm:px-6 pt-4 pb-2 mb-2";

  const roInput =
    "w-full h-10 rounded-xl px-4 border border-gray-200 bg-gray-100 text-[13px] font-normal text-gray-700";

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center text-gray-400 ${A.body}`}
      >
        {t("purchases.edit.loading")}
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header: Back, Title, X */}
      <div className={headerBar}>
        <div className="w-full max-w-md mx-auto flex items-center">
          <button
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
            type="button"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h2 className={`flex-1 text-center ${A.title}`}>{t("settings.companyProfile")}</h2>

          <button
            onClick={() => navigate(companyRoute(company_id, "/settings"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
            type="button"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md mx-auto px-2 sm:px-6 pb-10"
        autoComplete="off"
      >
        <div className={`${A.card} px-5 py-6`}>
          {/* Company Name (read-only) */}
          <div className="mb-4">
            <label className={`block mb-1 ${A.label}`}>{t("companyProfile.companyName")}</label>
            <input value={companyName} readOnly disabled className={roInput} />
          </div>

          {/* Financial Year End (read-only) */}
          <div className="mb-4">
            <label className={`block mb-1 ${A.label}`}>
              {t("companyProfile.financialYearEnd")}
            </label>
            <input value={fyeLabel} readOnly disabled className={roInput} />
          </div>

          {/* Currency */}
          <div className="mb-4">
            <AppleComboBox<CurrencyOpt>
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              label={t("companyProfile.currency")}
              options={CURRENCY_LIST}
              value={selectedCurrency}
              onChange={(opt) => setCurrency(opt ? opt.code : "HKD")}
              getOptionLabel={(opt) => `${opt.code} – ${t(DISPLAY_KEYS[opt.name as keyof typeof DISPLAY_KEYS])}`}
              placeholder={t("companyProfile.selectCurrency")}
              disabled={saving}
            />
          </div>

          {/* Industry */}
          <div className="mb-4">
            <AppleComboBox<PickOpt>
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              label={t("companyProfile.industry")}
              options={INDUSTRIES}
              value={selectedIndustry}
              onChange={(opt) => setIndustry(opt ? opt.value : "retail")}
              getOptionLabel={(opt) => t(DISPLAY_KEYS[opt.label as keyof typeof DISPLAY_KEYS])}
              placeholder={t("companyProfile.selectIndustry")}
              disabled={saving}
            />
          </div>

          {/* Language */}
          <div className="mb-2">
            <AppleComboBox<PickOpt>
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              label={t("accountMenu.language")}
              options={LANGUAGES}
              value={selectedLanguage}
              onChange={(opt) => setLanguage(opt ? opt.value : "en")}
              getOptionLabel={(opt) => t(DISPLAY_KEYS[opt.label as keyof typeof DISPLAY_KEYS])}
              placeholder={t("companyProfile.selectLanguage")}
              disabled={saving}
            />
          </div>

          {/* Status messages (reserved space) */}
          <div className="min-h-[28px] mt-3 text-center">
            {success && (
              <div className="text-emerald-700 text-[13px] font-medium">
                {t("companyProfile.saved")}
              </div>
            )}
            {error && (
              <div className="text-red-600 text-[13px] font-medium">
                {t("companyProfile.errors.save")}
              </div>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={saving}
          >
            {saving ? t("directory.common.savingDots") : t("directory.common.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}
